import { useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';

/** Inlined, so each card state can tint it instead of shipping two assets. */
function QuoteMark({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 167 119"
			className={cn('h-[119px] w-[167px] shrink-0', className)}
			fill="currentColor"
			aria-hidden
		>
			<path d="M84.5 119L132 0H167L139 119H84.5ZM0 119L47 0H82L54.5 119H0Z" />
		</svg>
	);
}

type Testimonial = { quote: string; name: string; role: string };

/**
 * The UI Kit's two colourways (`45:973`) are the rest and hover states of one card. Every colour is
 * declared on both sides, as a `group-hover` naming only one leaves the other inherited and drifting.
 */
function TestimonialCard({ item }: { item: Testimonial }) {
	return (
		<figure
			className={cn(
				// The strip's `--card`, which it also needs for its scroll room.
				'group relative flex h-[528px] w-[var(--card)] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[2rem] p-8 transition-colors duration-300',
				'md:h-[454px] md:p-10',
				'bg-sand hover:bg-ink focus-within:bg-ink',
			)}
		>
			<div aria-hidden className="pointer-events-none absolute top-4 left-4 md:top-6 md:left-6">
				<QuoteMark className="text-brand/10 group-focus-within:text-white/10 group-hover:text-white/10" />
			</div>

			{/* The quotes are the COMPONENT's: translators given them in a msgid drop one as often as not. */}
			<blockquote className="text-ink group-hover:text-olive group-focus-within:text-olive relative max-w-[482px] text-xl leading-[1.3] font-medium tracking-[-0.02em] transition-colors duration-300 md:text-2xl">
				“{item.quote}”
			</blockquote>

			<figcaption className="relative mt-8 flex flex-col leading-[1.5]">
				<span className="text-ink text-base font-medium tracking-[-0.02em] transition-colors duration-300 group-focus-within:text-white group-hover:text-white">
					{item.name}
				</span>
				<span className="text-ink/50 text-sm tracking-[-0.02em] transition-colors duration-300 group-focus-within:text-white/50 group-hover:text-white/50">
					{item.role}
				</span>
			</figcaption>
		</figure>
	);
}

export function Testimonials() {
	const { t } = useLingui();
	const scrollerRef = useRef<HTMLDivElement>(null);

	const scrollByCard = (direction: 1 | -1) => {
		const scroller = scrollerRef.current;
		const card = scroller?.firstElementChild;
		// The rendered card and gap (327/16 on a phone, 562/24 above `md`), so a click lands on a snap.
		const gap = scroller ? Number.parseFloat(getComputedStyle(scroller).columnGap) || 0 : 0;
		const step = card ? card.getBoundingClientRect().width + gap : 360;
		scroller?.scrollBy({ left: direction * step, behavior: 'smooth' });
	};

	const testimonials: Testimonial[] = [
		{
			quote: t`The impact of working with Worldcoo has been €392,000, which has let us cover more than 130,000 meals in our School Meal Grants programme for children in vulnerable situations`,
			name: 'Elisenda González',
			role: t`Child and adolescent protection specialist at Educo.`,
		},
		{
			quote: t`When we discovered the Solidarity Round-Up programme we thought it was a fantastic initiative. It lets society get involved in caring for the sea and the environment in a much more direct way. With very little, they can help collect huge amounts of waste from the beaches`,
			name: 'Gastón Cedrone',
			role: t`Head of Projects at Fundación Ecomar.`,
		},
		{
			// A real Red Cross quote, msgids and all; the hand-off's repeated one quote across two people.
			quote: t`With Charity Rounding Up we have been able to give advice for emotional well-being and psychosocial support to people in vulnerable situations affected by Covid.`,
			name: 'Jaime Gregori',
			role: t`Director of Fundraising, corporate alliances and social responsibility at Spanish Red Cross`,
		},
		// These speak about the model rather than a project of their own, so they come last.
		{
			quote: t`A very interesting and encouraging format. Instead of a fixed monthly fee, it gives the user complete freedom to contribute to a social project whenever they want. It also democratizes participation, with amounts accessible to everyone.`,
			name: 'Pau Gasol',
			role: t`NBA Player and Co-Founder of the Gasol Foundation`,
		},
		{
			quote: t`For me, Charity Rounding Up is a message of support that thousands of people send to those women who have been, or still are, mistreated by their partners: telling them that they are not alone and that, with that donation, they can carry on and they deserve to be happy.`,
			name: 'Ana Bella',
			role: t`Founder of Fundación Ana Bella`,
		},
		{
			quote: t`A very good initiative because, with little effort, a person feels they are contributing to a good cause. It also says a lot about the values of the company and the people who collaborate.`,
			name: 'Cristina Maragall',
			role: t`Spokesperson for the Pasqual Maragall Foundation`,
		},
	];

	return (
		<section className="bg-blush overflow-hidden py-12 md:py-24">
			<div className="mx-auto max-w-[1440px] px-6 xl:px-[54px]">
				{/* At 343px the heading wraps to three lines, so the arrows go under it. */}
				<div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
					<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`What they say about us`}
					</h2>
					<div className="flex shrink-0 gap-2">
						<button
							type="button"
							onClick={() => scrollByCard(-1)}
							aria-label={t`Previous testimonial`}
							className="border-ink text-ink hover:bg-ink/5 grid size-8 cursor-pointer place-items-center rounded-full border transition-colors"
						>
							<ArrowLeft className="size-4" />
						</button>
						<button
							type="button"
							onClick={() => scrollByCard(1)}
							aria-label={t`Next testimonial`}
							className="border-ink text-ink hover:bg-ink/5 grid size-8 cursor-pointer place-items-center rounded-full border transition-colors"
						>
							<ArrowRight className="size-4" />
						</button>
					</div>
				</div>
			</div>

			{/* Bleeds right only. The HERO's 54px gutter, as the drawn 32px read as no margin; `pr` lets the LAST card reach it. */}
			<div
				ref={scrollerRef}
				className="hide-scrollbar mt-6 flex snap-x scroll-pl-[var(--gutter)] gap-4 overflow-x-auto scroll-smooth pr-[max(0px,calc(100%-var(--gutter)-var(--card)))] pb-2 pl-[var(--gutter)] [--card:327px] [--gutter:max(1.5rem,calc((100%-1440px)/2+1.5rem))] md:gap-6 md:[--card:562px] xl:[--gutter:max(3.375rem,calc((100%-1440px)/2+3.375rem))]"
			>
				{testimonials.map((item) => (
					<TestimonialCard key={item.name} item={item} />
				))}
			</div>
		</section>
	);
}
